import React, { useState } from 'react';
import { ChevronDownIcon, BuildingOfficeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAuth, getTokenForWindow } from '../contexts/AuthContext';

interface Organization {
  id: string;
  name: string;
  role: string;
  is_primary: boolean;
}

interface OrganizationSwitcherProps {
  organizations: Organization[];
  currentOrgId: string;
  currentOrgName: string;
}

const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({
  organizations,
  currentOrgId,
  currentOrgName
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const { login } = useAuth();

  const handleSwitchOrganization = async (organizationId: string) => {
    if (organizationId === currentOrgId) {
      setIsOpen(false);
      return;
    }

    setSwitching(true);
    try {
      const token = getTokenForWindow();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL;
      if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_URL environment variable must be set');
      }
      const response = await fetch(`${API_BASE_URL}/api/auth/switch-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ organization_id: organizationId })
      });

      if (response.ok) {
        const { token: newToken } = await response.json();
        await login(newToken);
        setIsOpen(false);
        // Page will reload with new organization context
      } else {
        throw new Error('Failed to switch organization');
      }
    } catch (error) {
      console.error('Failed to switch organization:', error);
      alert('Failed to switch organization. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  // Always show switcher for now (for testing)
  if (organizations.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className="flex items-center w-full p-2 text-sm text-indigo-100 hover:bg-indigo-600 rounded-lg transition-colors disabled:opacity-50"
      >
        <BuildingOfficeIcon className="h-4 w-4 mr-2" />
        <span className="flex-1 text-left truncate">{currentOrgName}</span>
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => handleSwitchOrganization(org.id)}
              disabled={switching}
              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400" />
              <div className="flex-1 text-left">
                <div className="font-medium">{org.name}</div>
                <div className="text-xs text-gray-500 capitalize">{org.role}</div>
              </div>
              {org.id === currentOrgId && (
                <CheckIcon className="h-4 w-4 text-indigo-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrganizationSwitcher;